import { SphereIconButton } from "@components/common/sphere-icon-button";
import { useMarkupContext } from "@context-providers/markup/markup-context";
import { BaseMarkupProps } from "@custom-types/project-markups-types";
import { AttachmentIcon, blue, neutral } from "@flat-ui";
import { Box, Typography } from "@mui/material";
import { EDecimalToHex } from "@styles/common-colors";
import { addTransparency } from "@utils/ui-utils";
import { useState } from "react";

/**
 * The MarkupAttachments component shows the number of attachments and displays a different
 * message when hovered.
 * When clicked, it opens a side panel and updates the selected markup.
 *
 * @param {BaseMarkupProps} markup - The markup object containing the attachments.
 * @returns {JSX.Element} The rendered component displaying the number of attachments and an icon.
 */
export function MarkupAttachments({ markup }: BaseMarkupProps): JSX.Element {
  const [isHovered, setIsHovered] = useState<boolean>(false);

  const { updateSelectedMarkup, updateIsSidePanelOpen, updateActiveTab } =
    useMarkupContext();

  return (
    <Box
      component="div"
      sx={{ pl: "10px", display: "flex", alignItems: "center" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={(event) => {
        event.stopPropagation();

        updateIsSidePanelOpen(true);
        updateSelectedMarkup(markup);
        updateActiveTab("Attachments");
      }}
      data-testid="markup-attachments"
    >
      <Box
        component={"div"}
        sx={{ display: "flex", alignItems: "center", gap: "10px" }}
        data-testid="markup-attachments-content"
      >
        <SphereIconButton
          component={AttachmentIcon}
          buttonSize="22px"
          iconSize="14px"
          color={isHovered ? blue[500] : neutral[400]}
          iconButtonProps={{
            sx: {
              backgroundColor: isHovered
                ? addTransparency({
                    color: neutral[500],
                    alpha: EDecimalToHex.forty,
                  })
                : "none",
            },
          }}
        />

        {!isHovered && markup.attachmentsCount}
      </Box>

      {isHovered && (
        <Typography
          component="span"
          sx={{
            pl: "8px",
            fontSize: "12px",
            color: blue[500],
            textDecoration: "underline",
          }}
          data-testid="markup-attachments-label"
        >
          {markup.attachmentsCount ? "View attachments" : "Add attachments"}
        </Typography>
      )}
    </Box>
  );
}
